import { useEffect } from 'react';

import { fetchExchangeInfo } from '#/api/apiExchange';
import { useGlobalBalanceHistoryQueriesInvalidator } from '#/api/queries/balanceHistoryQueries';
import { useGlobalFillsPaginatedInvalidator } from '#/api/queries/fillPaginatedQueries';
import { useGlobalFillQueriesInvalidator } from '#/api/queries/fillQueries';
import { useGlobalMarketsQueriesInvalidator } from '#/api/queries/marketsQueries';
import { useGlobalOrdersQueriesInvalidator } from '#/api/queries/orderQueries';
import { useGlobalTickersQueriesInvalidator } from '#/api/queries/tickersQueries';
import { useGlobalVaultQueriesInvalidator } from '#/api/queries/vaultsQueries';
import { useGlobalPositionPnlQueriesInvalidator } from '#/api/queries/walletPositionsPnLQueries';
import { useGlobalPositionQueriesInvalidatorAndSync } from '#/api/queries/walletPositionsQueries';
import {
  startAuthenticatedSubscription,
  stopSubscription,
  restartWebsocket,
  isWebSocketInitialized,
  startPublicSubscription,
} from '#/api/services/webSocketApiService';
import { RELOAD_EXCHANGE_STATUS_CONTROLS_DELAY_MS } from '#/config/constants';
import { fetchAndDispatchUser } from '#/reduxState/currentUser/currentUserUtils';
import { changeExchangeInfo } from '#/reduxState/exchange/exchangeInfo';
import { useSelector, staticDispatch } from '#/reduxState/store';

import {
  useActiveWindowInterval,
  useSelectorActiveChain,
  useWebsocketEventListener,
} from './hooks';
import {
  useListenOnWebsocketReloadMarkets,
  useMarketsAsyncFetcher,
} from './markets/marketsAsyncFetcher';

function WebsocketSubscriptionManager() {
  const websocketConnected = useSelector(
    state => state.exchange.websocketState.connected
  );
  const userInitialized = useSelector(state => state.currentUser.initialized);
  const loggedIn = useSelector(state => state.currentUser.loggedIn);
  const userId = useSelector(state => state.currentUser.id);
  const walletAddressInStore = useSelector(state => state.wallet.walletAddress);
  const walletAddress = userId ? walletAddressInStore || undefined : undefined;
  const walletType = useSelector(state => state.wallet.walletType);
  const { activeChain } = useSelectorActiveChain();

  useGlobalOrdersQueriesInvalidator();
  useGlobalFillQueriesInvalidator();
  useGlobalFillsPaginatedInvalidator();
  useGlobalPositionQueriesInvalidatorAndSync();
  useGlobalPositionPnlQueriesInvalidator();
  useGlobalBalanceHistoryQueriesInvalidator();
  useGlobalVaultQueriesInvalidator();
  useGlobalTickersQueriesInvalidator();
  useGlobalMarketsQueriesInvalidator();

  useEffect(() => {
    if (!isWebSocketInitialized()) {
      // useEffect run twice initially in strict mode (only dev mode)
      console.debug(
        '[ws] Websocket not initialized (Expected on Dev Strict mode)'
      );
      return;
    }
    restartWebsocket(walletAddress);
  }, [walletAddress]);

  // Receive user profile updates
  useEffect(() => {
    if (!websocketConnected) {
      return;
    }
    if (walletAddress) {
      startAuthenticatedSubscription({ name: 'webclient' });
    } else {
      startPublicSubscription({ name: 'webclient' });
    }
    return () => {
      stopSubscription({ name: 'webclient' });
    };
  }, [userId, walletAddress, websocketConnected]);

  // Receive all wallet order / fill updates
  useEffect(() => {
    if (!walletAddress || !websocketConnected) {
      return;
    }
    startAuthenticatedSubscription([
      { name: 'orders' },
      { name: 'positions' },
      { name: 'deposits' },
      { name: 'withdrawals' },
    ]);
    // { name: 'balances', wallet: walletAddress },
    return () => {
      // stopSubscription({ name: 'balances' });
      stopSubscription({ name: 'orders' });
      stopSubscription({ name: 'positions' });
      stopSubscription({ name: 'deposits' });
      stopSubscription({ name: 'withdrawals' });
    };
  }, [loggedIn, walletAddress, websocketConnected]);

  // Fetch markets and keep them in sync
  // Markets are required on multiple places
  //  - BuySellPanel (limitMaker postOnly mode, sidebar listing of quotes, Balances page, Chart search)
  useMarketsAsyncFetcher();
  useListenOnWebsocketReloadMarkets();

  useEffect(() => {
    if (activeChain !== 'katanaEVMChain') {
      return;
    }
    // Lets fetch some basic information eg. ETH price, rewards, funds globally
    fetchExchangeInfo(activeChain, walletAddress).then(exchangeInfo =>
      staticDispatch(changeExchangeInfo(exchangeInfo, activeChain))
    );
  }, [activeChain, walletAddress]);

  useWebsocketEventListener('webclient', message => {
    if (activeChain !== 'katanaEVMChain') {
      return;
    }
    if (
      message.data.event === 'exchange_status_updated' &&
      // controls_market (intermittent market session open/close) does not
      // affect exchange info and would trigger multiple fetches when more
      // intermittent markets open at once
      message.data.action !== 'controls_market'
    ) {
      setTimeout(
        () =>
          fetchExchangeInfo(activeChain, walletAddress).then(exchangeInfo =>
            staticDispatch(changeExchangeInfo(exchangeInfo, activeChain))
          ),
        RELOAD_EXCHANGE_STATUS_CONTROLS_DELAY_MS
      );
    }
  });

  useActiveWindowInterval(() => {
    if (activeChain !== 'katanaEVMChain') {
      return;
    }
    fetchExchangeInfo(activeChain, walletAddress).then(exchangeInfo =>
      staticDispatch(changeExchangeInfo(exchangeInfo, activeChain))
    );
  }, 15_000);

  useEffect(() => {
    if (!userInitialized) {
      return;
    }
    // setMonitoringUserContext({
    //   userId: `${userId}`,
    //   walletAddress: walletAddressInStore,
    //   walletType,
    // });
  }, [userId, walletAddressInStore, walletType, activeChain, userInitialized]);

  useActiveWindowInterval(() => {
    if (!userInitialized) {
      // Retry to load user again if is not intialized after 18 seconds
      fetchAndDispatchUser(staticDispatch);
    }
  }, 18_000);

  return null;
}

export default WebsocketSubscriptionManager;
